import type { GameState } from "@dailywars/core/engine";
import { createContext, use, useMemo, type ReactNode } from "react";
import { useStore } from "zustand/react";
import { useShallow } from "zustand/react/shallow";

import {
  createGameStore,
  DEFAULT_GAME_SETTINGS,
  type GameStore,
  type GameStoreApi,
} from "#/game/store";
import type { GameSettings } from "#/game/store/types";

const GameStoreContext = createContext<GameStoreApi | null>(null);

export function GameStoreProvider({
  initialState,
  initialServerDayNumber,
  settings = DEFAULT_GAME_SETTINGS,
  children,
}: {
  initialState: GameState;
  initialServerDayNumber: number;
  settings?: GameSettings;
  children: ReactNode;
}) {
  const { fastPlayback, endTurnConfirmation, disableAiPlayback, captureResetWarning } = settings;
  const store = useMemo(
    () =>
      createGameStore({
        initialState,
        initialServerDayNumber,
        settings: { fastPlayback, endTurnConfirmation, disableAiPlayback, captureResetWarning },
      }),
    [
      initialState,
      initialServerDayNumber,
      fastPlayback,
      endTurnConfirmation,
      disableAiPlayback,
      captureResetWarning,
    ],
  );
  return <GameStoreContext value={store}>{children}</GameStoreContext>;
}

export function useGameStoreApi(): GameStoreApi {
  const api = use(GameStoreContext);
  if (!api) throw new Error("useGameStoreApi must be used inside <GameStoreProvider>");
  return api;
}

export function useGameSelector<T>(selector: (s: GameStore) => T): T {
  return useStore(useGameStoreApi(), selector);
}

export { useShallow };
